/**
 * Notebook domain model — tab layout, API row mapping, theme JSON.
 */

import { migrateSectionPayload } from '../registry/blocks-registry-migrate';
import { resolveBlockType } from '../registry/blocks-registry-resolve';
import { HERO_BLOCK_DEFAULTS } from '../Blocks/shared/defaults/block-seeds';
import {
  createEmptyTabsBlockData,
  mergeTabBlocksIntoSectionData,
} from './tabs-model';

const THEME_VERSION = 1;
const MAX_TABS = 8;
const LS_KEY_PREFIX = "folio_notebook_";

export { MAX_TABS, LS_KEY_PREFIX };

export function lsKey(notebookId) {
  return `${LS_KEY_PREFIX}${notebookId}`;
}

export function createTab(label = "Tab 1") {
  return { id: crypto.randomUUID(), label, sections: [] };
}

function createDefaultNotebookTabs() {
  return [1, 2, 3, 4].map((index) => createTab(`Tab ${index}`));
}

function createStructuralSections() {
  return [
    {
      id: crypto.randomUUID(),
      type: "block_hero",
      data: { ...HERO_BLOCK_DEFAULTS },
      sort_key: "a0",
    },
    {
      id: crypto.randomUUID(),
      type: "block_tabs",
      data: createEmptyTabsBlockData(),
      sort_key: "a1",
    },
  ];
}

function isBlankTemplateNotebook(notebook) {
  if (
    !notebook ||
    !Array.isArray(notebook.tabs) ||
    notebook.tabs.length !== 1
  ) {
    return false;
  }

  const [tab] = notebook.tabs;
  if (!tab || !Array.isArray(tab.sections)) return false;

  return tab.sections.every(
    (section) =>
      resolveBlockType(section.type) === "block_hero" ||
      resolveBlockType(section.type) === "block_tabs",
  );
}

export function expandBlankNotebookTabs(notebook) {
  if (!isBlankTemplateNotebook(notebook)) return notebook;

  const [firstTab, ...otherTabs] = createDefaultNotebookTabs();
  return {
    ...notebook,
    tabs: [{ ...firstTab, sections: notebook.tabs[0].sections }, ...otherTabs],
  };
}

export function createEmptyNotebook(id) {
  const [tab, ...otherTabs] = createDefaultNotebookTabs();
  tab.sections = createStructuralSections();
  return {
    id,
    title: "",
    subtitle: "",
    emoji: "📓",
    is_published: false,
    public_slug: null,
    tabs: [tab, ...otherTabs],
  };
}

/** @typedef {{ id: string, type: string, data: unknown, sort_key?: string, page_id?: string|null }} ApiSection */

/**
 * @param {unknown} theme
 * @returns {{ activeTabId?: string, tabs: { id: string, label: string }[] } | null}
 */
export function parseTheme(theme) {
  if (!theme || typeof theme !== "object") return null;
  const t = /** @type {Record<string, unknown>} */ (theme);
  if (t.v !== THEME_VERSION || !Array.isArray(t.tabs)) return null;
  const tabs = t.tabs
    .filter((x) => x && typeof x === "object" && typeof x.id === "string")
    .map((x) => ({
      id: String(x.id),
      label: typeof x.label === "string" ? x.label : "Tab",
    }));
  if (!tabs.length) return null;
  const sectionTabs =
    t.sectionTabs && typeof t.sectionTabs === "object"
      ? /** @type {Record<string, string>} */ (t.sectionTabs)
      : {};

  return {
    activeTabId: typeof t.activeTabId === "string" ? t.activeTabId : tabs[0].id,
    tabs,
    sectionTabs,
  };
}

export function buildTheme(notebook, activeTabId) {
  /** @type {Record<string, string>} */
  const sectionTabs = {};
  for (const tab of notebook.tabs) {
    for (const sec of tab.sections) {
      sectionTabs[sec.id] = tab.id;
    }
  }
  return {
    v: THEME_VERSION,
    activeTabId: activeTabId ?? notebook.tabs[0]?.id ?? null,
    tabs: notebook.tabs.map(({ id, label }) => ({ id, label })),
    sectionTabs,
  };
}

/**
 * Hydrate store notebook shape from API notebook + flat sections.
 * @param {string} id
 * @param {{ title?: string, icon?: string|null, description?: string|null, theme?: unknown }} apiNotebook
 * @param {ApiSection[]} apiSections
 * @param {Array<{ id: string, section_id: string, tab_id?: string, type: string, content?: unknown, sort_key?: string }>} [apiBlocks]
 */
export function notebookFromApi(id, apiNotebook, apiSections, apiBlocks = []) {
  const parsed = parseTheme(apiNotebook?.theme);
  const tabs = parsed
    ? parsed.tabs.map((t) => ({ ...t, sections: [] }))
    : createDefaultNotebookTabs().map((t, index) => ({
        ...t,
        sections: index === 0 ? createStructuralSections() : [],
      }));

  const sorted = [...(apiSections ?? [])].sort((a, b) =>
    (a.sort_key ?? "") < (b.sort_key ?? "") ? -1 : 1,
  );

  const sectionTabs = parsed?.sectionTabs ?? {};
  const seenSectionIds = new Set();
  const tabsBlockPlaced = new Set();

  for (const row of sorted) {
    if (seenSectionIds.has(row.id)) continue;
    seenSectionIds.add(row.id);

    let tabId = tabs[0].id;
    if (sectionTabs[row.id] && tabs.some((t) => t.id === sectionTabs[row.id])) {
      tabId = sectionTabs[row.id];
    } else if (row.page_id && tabs.some((t) => t.id === row.page_id)) {
      tabId = row.page_id;
    }
    const tab = tabs.find((t) => t.id === tabId) ?? tabs[0];
    const blockType = resolveBlockType(row.type);

    if (blockType === "block_tabs") {
      if (tabsBlockPlaced.has(tab.id)) continue;
      tabsBlockPlaced.add(tab.id);
    }

    let data = migrateSectionPayload(row.type, row.data ?? {});
    if (blockType === "block_tabs") {
      const childRows = (apiBlocks ?? []).filter(
        (b) => b.section_id === row.id,
      );
      data = mergeTabBlocksIntoSectionData(data, childRows);
    }
    tab.sections.push({
      id: row.id,
      type: blockType,
      data,
      sort_key: row.sort_key,
    });
  }

  return {
    notebook: expandBlankNotebookTabs({
      id,
      title: apiNotebook?.title ?? "",
      subtitle: apiNotebook?.description ?? "",
      emoji: apiNotebook?.icon ?? "📓",
      is_published: Boolean(apiNotebook?.is_published),
      public_slug: apiNotebook?.public_slug ?? null,
      public_short_id: apiNotebook?.public_short_id ?? null,
      tabs,
    }),
    activeTabId: parsed?.activeTabId ?? tabs[0].id,
  };
}

/**
 * Flatten tabs → API section rows (preserves sort_key on each section).
 */
export function flattenSections(notebook) {
  /** @type {ApiSection[]} */
  const out = [];
  for (const tab of notebook.tabs) {
    for (const sec of tab.sections) {
      out.push({
        id: sec.id,
        type: sec.type,
        data: sec.data,
        sort_key: sec.sort_key,
        page_id: tab.id,
      });
    }
  }
  return out;
}

export function getActiveTab(notebook, activeTabId) {
  return notebook.tabs.find((t) => t.id === activeTabId) ?? notebook.tabs[0];
}

/** Find which notebook theme tab owns a section id. */
export function findTabIdForSection(notebook, sectionId) {
  for (const tab of notebook.tabs) {
    if (tab.sections.some((s) => s.id === sectionId)) return tab.id;
  }
  return null;
}

export function snapshotForExport(notebook, activeTabId) {
  return {
    ...notebook,
    activeTabId,
    exportedAt: new Date().toISOString(),
  };
}
